import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useUserIntegrations } from '@/common/hooks/useUserIntegrations/useUserIntegrations';
import { GoogleButton } from '@/components/GoogleContactActions/GoogleButton';
import {
  getContactPayload,
  googleContactDataKeys,
  saveOrCreateGoogleContact,
} from '@/components/GoogleContactActions/helpers';
import { useIsGoogleContactOwner } from '@/components/GoogleContactActions/useIsGoogleContactOwner';
import { GENERIC_COVER_SHEET_ERROR } from '@/views/Coversheet/constants/coverSheet';
import { useReferralContext } from '@/views/Referrals/common/ReferralContext';
import {
  findAgentTeamMembership,
  getFirstAgent,
  getSecondAgent,
  getSecondAgentFieldName,
} from '@/views/Referrals/utils';
import type { FieldValues } from '@/views/Referrals/views/NewReferral/types';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';

interface Props {
  disabled?: boolean;
}

export function GoogleContactActions({ disabled = false }: Props) {
  const { setValue } = useFormContext();
  const { submitAsAgents } = useReferralContext();

  const trackEvent = useTrackReferralsAnalytics();

  const [referralType, referringAgent, transactingAgent] = useWatch<
    FieldValues,
    ['referralType', 'referringAgent', 'transactingAgent']
  >({
    name: ['referralType', 'referringAgent', 'transactingAgent'],
  });

  const secondAgentFieldName = getSecondAgentFieldName({ referralType });
  const firstAgent = getFirstAgent({
    referralType,
    referringAgent,
    transactingAgent,
  });
  const secondAgent = getSecondAgent({
    referralType,
    referringAgent,
    transactingAgent,
  });
  const selectedAgent = findAgentTeamMembership(firstAgent, submitAsAgents);

  const [mutationLoading, setMutationLoading] = useState(false);

  const [initialFields, setInitialFields] = useState<unknown[]>();
  const contactIdFieldName = `${secondAgentFieldName}.googleContactId` as const;
  const googleContactId = secondAgent?.googleContactId;
  const contactFields = googleContactDataKeys.map((field) =>
    get(secondAgent, field, ''),
  );

  useEffect(() => {
    if (contactFields && !initialFields) {
      setInitialFields(contactFields);
    }
  }, [contactFields, initialFields]);

  const { isIntegrationValid } = useUserIntegrations(selectedAgent?.id);
  const isGoogleLinked = isIntegrationValid('googlePeople');
  const isOwner = useIsGoogleContactOwner(selectedAgent?.id);

  if (!selectedAgent?.id || !isGoogleLinked || !isOwner) {
    return null;
  }

  const handleSave = async () => {
    trackEvent(TrackReferralsEvent.ClickSaveGoogleContactButton, {
      contactId: null,
    });

    const contactPayload = getContactPayload(contactFields);

    setMutationLoading(true);
    try {
      const newGoogleContactId = await saveOrCreateGoogleContact({
        ownerId: selectedAgent.id,
        contact: contactPayload,
      });

      toast('success', 'Google Contact has been created');
      setValue(contactIdFieldName, newGoogleContactId);
      setInitialFields(contactFields);
    } catch (error) {
      toast('warning', GENERIC_COVER_SHEET_ERROR, {
        showCloseButton: true,
      });
      datadogRum.addError(error, {
        contactPayload,
      });
    } finally {
      setMutationLoading(false);
    }
  };

  const handleUpdate = async () => {
    trackEvent(TrackReferralsEvent.ClickSaveGoogleContactButton, {
      contactId: googleContactId,
    });

    const contactPayload = getContactPayload(contactFields);

    setMutationLoading(true);
    try {
      await saveOrCreateGoogleContact({
        ownerId: selectedAgent.id,
        googleContactId,
        contact: contactPayload,
      });

      toast('success', 'Google Contact has been updated');
      setInitialFields(contactFields);
    } catch (error) {
      toast('warning', GENERIC_COVER_SHEET_ERROR, {
        showCloseButton: true,
      });
      datadogRum.addError(error, {
        contactPayload,
      });
    } finally {
      setMutationLoading(false);
    }
  };

  const isDirty = !!contactFields.find(
    (value, fieldIndex) => value !== initialFields?.[fieldIndex],
  );

  return (
    <GoogleButton
      handleSaveGoogleContact={handleSave}
      handleUpdateGoogleContact={handleUpdate}
      isDirty={isDirty}
      loading={mutationLoading}
      disabled={disabled}
      hasGoogleId={Boolean(googleContactId)}
    />
  );
}
